import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ScrollToTop } from "@/components/ScrollToTop";
import GtmPageView from "@/components/analytics/GtmPageView";

import PageTransition from "@/components/layout/PageTransition";
import DisabledPageGate from "@/components/layout/DisabledPageGate";
const AiChatWidget = lazy(() => import("@/components/ai/AiChatWidget"));
// Notisytorna behövs inte för första visningen – laddas efter hydrering.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

function ToastersMount() {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
  );
}
const SocialProofCounter = lazy(() => import("@/components/common/SocialProofCounter"));


import { useLocation } from "@/lib/router-compat";
import { useSocialProofConfig } from "@/hooks/useSocialProofConfig";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAiChatConfig } from "@/hooks/useAiChatConfig";
import { passesDisplayRules } from "@/lib/displayRules";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { onAiChatRequested } from "@/components/ai/aiChatBus";
import { installGlobalImageFallback } from "@/lib/articleImage";
import { CONSENT_BOOTSTRAP } from "@/lib/consent";
import ConsentBanner from "@/components/consent/ConsentBanner";
import { SITE_URL } from "@/config/urlHierarchy";
import { allSeoOverrides } from "@/config/seoOverrideStore";
import appCss from "../styles.css?url";
import figtreeLatin from "@/assets/fonts/figtree-latin.woff2?url";

const NotFound = lazy(() => import("@/pages/NotFound"));

/** Höj vid ny favicon/ikonuppsättning så klienter hämtar om direkt. */
const ICON_VERSION = "4";


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Hämtas på servern så att avstängda verktyg aldrig renderas eller länkas
  // i serverns HTML.
  loader: async () => {
    const { fetchToolsConfig } = await import("@/hooks/useToolsConfig");
    try {
      return { toolsConfig: await fetchToolsConfig() };
    } catch {
      const { defaultToolsConfig } = await import("@/config/toolsRegistry");
      return { toolsConfig: defaultToolsConfig() };
    }
  },
  staleTime: 60_000,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "format-detection", content: "telephone=no" },
      { title: "Elmarknad.se » Sveriges största jämförelsetjänst inom el & energi" },
      { name: "author", content: "Elmarknad.se" },
      {
        name: "google-site-verification",
        content: "r5UfU36J3Ux1D3Z80qs9xY_iFutcxbIY5RYehBN4W_Y",
      },
      { name: "theme-color", content: "#0B1F3A" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "Elmarknad" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "application-name", content: "Elmarknad.se" },

      { property: "og:image", content: `${SITE_URL}/og-image.png` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      {
        property: "og:image:alt",
        content: "Elmarknad.se – Sveriges största jämförelsetjänst inom el & energi",
      },
      { property: "og:site_name", content: "Elmarknad.se" },
      { property: "og:locale", content: "sv_SE" },

      { name: "twitter:image", content: `${SITE_URL}/og-image.png` },
      {
        name: "twitter:image:alt",
        content: "Elmarknad.se – Sveriges största jämförelsetjänst inom el & energi",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@elmarknad" },
      { property: "og:type", content: "website" },

    ],
    scripts: [
      ...(allSeoOverrides().length > 0
        ? [
            {
              children: `window.__SEO_OVERRIDES__=${JSON.stringify(allSeoOverrides()).replace(/</g, "\\u003c")};`,
            },
          ]
        : []),
      {
        // Google Consent Mode v2: standardläge "denied" och ett eventuellt sparat
        // cookieval sätts INNAN GTM laddas (se src/lib/consent.ts).
        children: CONSENT_BOOTSTRAP,
      },
      {
        // GTM startar när sidan har laddat klart och webbläsaren är ledig, så att
        // den inte konkurrerar med sidans eget innehåll. Händelser som skickas
        // innan dess köas i dataLayer. Samtycket hanteras av vår egen banner.
        children: `(function(w,d,s,l,i){if(w.__emGtmBoot)return;w.__emGtmBoot=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var done=false;function go(){if(done)return;done=true;var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);}function idle(){if('requestIdleCallback' in w){w.requestIdleCallback(go,{timeout:3000});}else{setTimeout(go,1500);}}if(d.readyState==='complete'){idle();}else{w.addEventListener('load',idle,{once:true});}})(window,document,'script','dataLayer','GTM-5KCMKSZ');`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Elmarknad.se",
              url: `${SITE_URL}/`,
              logo: `${SITE_URL}/lovable-uploads/elmarknad-logo-2025.png`,
              description:
                "Jämförelsetjänst inom el och energi: elavtal, solceller, batterier, värmepumpar och elbilsladdare.",
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              name: "Elmarknad.se",
              url: `${SITE_URL}/`,
              inLanguage: "sv-SE",
              publisher: { "@id": `${SITE_URL}/#organization` },
            },
          ],
        }),
      },
    ],
    links: [
      // Teckensnittet förladdas så att texten inte byter typsnitt efter första rendering.
      { rel: "preload", href: figtreeLatin, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: `/favicon.ico?v=${ICON_VERSION}`, sizes: "16x16 32x32 48x48 96x96" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: `/icons/icon-16.png?v=${ICON_VERSION}` },
      { rel: "icon", type: "image/png", sizes: "32x32", href: `/icons/icon-32.png?v=${ICON_VERSION}` },
      { rel: "icon", type: "image/png", sizes: "48x48", href: `/icons/icon-48.png?v=${ICON_VERSION}` },
      { rel: "icon", type: "image/png", sizes: "96x96", href: `/icons/icon-96.png?v=${ICON_VERSION}` },
      { rel: "icon", type: "image/png", sizes: "192x192", href: `/icons/icon-192.png?v=${ICON_VERSION}` },
      { rel: "icon", type: "image/png", sizes: "512x512", href: `/icons/icon-512.png?v=${ICON_VERSION}` },
      { rel: "apple-touch-icon", sizes: "180x180", href: `/icons/apple-touch-icon.png?v=${ICON_VERSION}` },

      { rel: "manifest", href: `/manifest.webmanifest?v=${ICON_VERSION}` },


    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => (
    <Suspense fallback={<div className="min-h-dvh bg-background" />}>
      <NotFound />
    </Suspense>
  ),
  errorComponent: (props) => <RootErrorComponent error={props.error as Error} reset={props.reset} />,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="sv" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-5KCMKSZ"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    installGlobalImageFallback();
  }, []);
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ConsentBanner />
          <ToastersMount />
          <ScrollToTop />
          <GtmPageView />

          <AppOutlet />
          <AiChatMount />
          <SocialProofMount />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

/** Sant när sidan har laddat klart och webbläsaren har en ledig stund. */
function useIdleAfterLoad(timeout = 3000) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const go = () => {
      const ric = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (ric) ric(() => !cancelled && setIdle(true), { timeout });
      else setTimeout(() => !cancelled && setIdle(true), 1500);
    };
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", go);
    };
  }, [timeout]);
  return idle;
}

function AiChatMount() {
  const location = useLocation();
  const pathname = location.pathname;
  const { config } = useAiChatConfig();
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  // Widgeten (med markdown-renderaren) laddas först när sidan är klar eller
  // när någon försöker öppna chatten, i stället för under sidladdningen.
  const idle = useIdleAfterLoad();
  const [requested, setRequested] = useState(false);
  useEffect(() => onAiChatRequested(() => setRequested(true)), []);

  const delayMs = Math.max(0, config.delaySeconds) * 1000;
  const showOnScroll = config.showOnScroll;

  useEffect(() => {
    setVisible(delayMs === 0);
    if (delayMs === 0) return;
    const timer = setTimeout(() => setVisible(true), delayMs);
    const onScroll = () => {
      if (showOnScroll && window.scrollY > 0) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [delayMs, showOnScroll]);

  if (pathname.startsWith("/admin")) return null;
  // På den egna chattsidan ska den flytande widgeten aldrig synas eller öppnas.
  if (pathname === "/ai" || pathname.startsWith("/elmarknad-ai")) return null;
  // Även när chatten är avstängd i admin monteras widgeten (utan synlig
  // chattbubbla) så att den kan öppnas via AI-sökfältet i headern eller
  // från Elmarknad AI-sidan.
  const showLauncher =
    config.enabled &&
    visible &&
    passesDisplayRules(
      { includePaths: config.includePaths, excludePaths: config.excludePaths, device: config.device },
      pathname,
      Boolean(isMobile),
    );
  if (!idle && !requested) return null;
  return (
    <Suspense fallback={null}>
      <AiChatWidget showLauncher={showLauncher} />
    </Suspense>
  );
}

function SocialProofMount() {
  const location = useLocation();
  const { config } = useSocialProofConfig();

  if (location.pathname.startsWith("/admin")) return null;
  if (!config.enabled) return null;
  return (
    <Suspense fallback={null}>
      <SocialProofCounter />
    </Suspense>
  );
}

function AppOutlet() {
  const location = useLocation();
  usePageViewTracking();
  return (
    <PageTransition key={location.pathname}>
      <Suspense fallback={<div className="min-h-dvh bg-background" />}>
        <DisabledPageGate notFound={<NotFound />}>
          <Outlet />
        </DisabledPageGate>
      </Suspense>
    </PageTransition>
  );
}




function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  console.error(error);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-dvh bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="heading-1">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex items-center justify-center gap-2">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a href="/" className="px-4 py-2 rounded-xl border border-border bg-card text-foreground">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
